import React, { useState, useContext } from 'react';
import { API_URL } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../../context/ThemeContext';
import {
  Container, FormBox, Title, FormGroup, Label,
  Input, Button, ErrorMessage, LinkText,
} from './SignUp.styles';

function SignUp() {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: ''
  });
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [aceptaPriv,     setAceptaPriv]     = useState(false);
  const [leyoTerminos,       setLeyoTerminos]       = useState(false);
  const [leyoCondPublicador, setLeyoCondPublicador] = useState(false);
  const [leyoPrivacidad,     setLeyoPrivacidad]     = useState(false);
  const [leyoCookies,        setLeyoCookies]        = useState(false);
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (!aceptaTerminos || !aceptaPriv) {
      setError('Debes aceptar los Términos y la Política de Privacidad para registrarte');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/registro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre:           formData.name,
          email:            formData.email,
          password:         formData.password,
          acepta_terminos:  true,
          acepta_privacidad: true,
          version_legal:    '1.0',
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error en el registro'); setLoading(false); return; }
      navigate('/signin');
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
      setLoading(false);
    }
  };

  const bg = theme.modalBg || theme.background;

  return (
    <Container bgColor={theme.background}>
      <Helmet>
        <title>Crear cuenta · Trastero</title>
        <meta name="description" content="Únete a Trastero y empieza a vender tus artículos de segunda mano de forma fácil y segura." />
      </Helmet>
      <FormBox bgColor={bg}>
        <Title bgColor={bg}>Crear Cuenta</Title>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name" bgColor={bg}>Nombre Completo</Label>
            <Input type="text" id="name" name="name"
              value={formData.name} onChange={handleChange}
              placeholder="Tu nombre completo" accentColor={theme.accent} bgColor={bg} />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email" bgColor={bg}>Correo Electrónico</Label>
            <Input type="email" id="email" name="email"
              value={formData.email} onChange={handleChange}
              placeholder="tu@email.com" accentColor={theme.accent} bgColor={bg} />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password" bgColor={bg}>Contraseña</Label>
            <Input type="password" id="password" name="password"
              value={formData.password} onChange={handleChange}
              placeholder="••••••••" accentColor={theme.accent} bgColor={bg} />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword" bgColor={bg}>Confirmar Contraseña</Label>
            <Input type="password" id="confirmPassword" name="confirmPassword"
              value={formData.confirmPassword} onChange={handleChange}
              placeholder="••••••••" accentColor={theme.accent} bgColor={bg} />
          </FormGroup>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '8px 0' }}>
            {/* Bloque 1: Términos + Condiciones del Publicador */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, color: theme.accent + 'cc' }}>
                Lee los documentos obligatorios antes de aceptar:
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 4 }}>
                <a href="/legal/terminos" target="_blank" rel="noreferrer"
                  onClick={() => setLeyoTerminos(true)}
                  style={{ color: theme.accent, fontSize: 13, fontWeight: 600 }}>
                  📄 Términos y Condiciones
                </a>
                <a href="/legal/condiciones-publicador" target="_blank" rel="noreferrer"
                  onClick={() => setLeyoCondPublicador(true)}
                  style={{ color: theme.accent, fontSize: 13, fontWeight: 600 }}>
                  📄 Condiciones del Publicador
                </a>
              </div>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                cursor: (leyoTerminos && leyoCondPublicador) ? 'pointer' : 'not-allowed',
                fontSize: 13,
                color: (leyoTerminos && leyoCondPublicador) ? theme.accent + 'cc' : theme.accent + '44',
                opacity: (leyoTerminos && leyoCondPublicador) ? 1 : 0.5,
              }}>
                <input type="checkbox"
                  checked={aceptaTerminos}
                  disabled={!leyoTerminos || !leyoCondPublicador}
                  onChange={e => setAceptaTerminos(e.target.checked)}
                  title={(leyoTerminos && leyoCondPublicador) ? '' : 'Abre y lee los documentos antes de aceptar'}
                  style={{ marginTop: 2, accentColor: theme.accent, flexShrink: 0 }} />
                <span>
                  He leído y acepto los Términos y Condiciones y las Condiciones del Publicador *
                  {(!leyoTerminos || !leyoCondPublicador) && (
                    <span style={{ display: 'block', fontSize: 11, color: theme.accent + '88', marginTop: 2 }}>
                      Abre los documentos anteriores para activar esta casilla
                    </span>
                  )}
                </span>
              </label>
            </div>

            {/* Bloque 2: Privacidad + Cookies */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 4 }}>
                <a href="/legal/privacidad" target="_blank" rel="noreferrer"
                  onClick={() => setLeyoPrivacidad(true)}
                  style={{ color: theme.accent, fontSize: 13, fontWeight: 600 }}>
                  📄 Política de Privacidad
                </a>
                <a href="/legal/cookies" target="_blank" rel="noreferrer"
                  onClick={() => setLeyoCookies(true)}
                  style={{ color: theme.accent, fontSize: 13, fontWeight: 600 }}>
                  📄 Política de Cookies
                </a>
              </div>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                cursor: (leyoPrivacidad && leyoCookies) ? 'pointer' : 'not-allowed',
                fontSize: 13,
                color: (leyoPrivacidad && leyoCookies) ? theme.accent + 'cc' : theme.accent + '44',
                opacity: (leyoPrivacidad && leyoCookies) ? 1 : 0.5,
              }}>
                <input type="checkbox"
                  checked={aceptaPriv}
                  disabled={!leyoPrivacidad || !leyoCookies}
                  onChange={e => setAceptaPriv(e.target.checked)}
                  title={(leyoPrivacidad && leyoCookies) ? '' : 'Abre y lee los documentos antes de aceptar'}
                  style={{ marginTop: 2, accentColor: theme.accent, flexShrink: 0 }} />
                <span>
                  He leído y acepto la Política de Privacidad y la Política de Cookies *
                  {(!leyoPrivacidad || !leyoCookies) && (
                    <span style={{ display: 'block', fontSize: 11, color: theme.accent + '88', marginTop: 2 }}>
                      Abre los documentos anteriores para activar esta casilla
                    </span>
                  )}
                </span>
              </label>
            </div>

            <p style={{ fontSize: 11, color: theme.accent + '66', margin: 0 }}>* Campos obligatorios. Sin aceptar estos documentos no es posible crear una cuenta.</p>
          </div>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" disabled={loading || !aceptaTerminos || !aceptaPriv} accentColor={theme.accent}>
            {loading ? 'Creando cuenta...' : 'Registrarse'}
          </Button>
        </form>

        <LinkText accentColor={theme.accent} bgColor={bg}>
          ¿Ya tienes cuenta? <a href="/signin">Inicia sesión aquí</a>
        </LinkText>
      </FormBox>
    </Container>
  );
}

export default SignUp;
